import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      category: 'Bookings & Reservations',
      icon: 'Calendar',
      faqs: [
        {
          question: 'How can I make a reservation?',
          answer: `You can make a reservation through our online booking system, by calling us at +91 98765 43210, or by sending us a WhatsApp message. We recommend booking in advance, especially during peak seasons (October to March).`
        },
        {
          question: 'What is your cancellation policy?',
          answer: `Free cancellation up to 48 hours before check-in. Cancellations within 48 hours are subject to a 50% charge. No-shows will be charged the full amount. During peak seasons, different terms may apply.`
        },
        {
          question: 'Do you offer group discounts?',
          answer: `Yes! We offer special rates for groups of 10 or more people. Corporate packages and team-building activities are also available. Contact our sales team for customized group packages.`
        },
        {
          question: 'What payment methods do you accept?',
          answer: `We accept all major credit cards, debit cards, UPI payments, net banking, and cash. Online payments are processed securely through our payment gateway partners.`
        }
      ]
    },
    {
      category: 'Accommodation & Facilities',
      icon: 'Bed',
      faqs: [
        {
          question: 'What types of accommodations do you offer?',
          answer: `We offer Deluxe Rooms, River View Cottages, Luxury Tents, and unique Tree Houses. All accommodations include modern amenities, private bathrooms, and scenic views of the surrounding nature.`
        },
        {
          question: 'Are meals included in the stay?',
          answer: `We offer various meal plans including breakfast only, half board (breakfast + dinner), and full board (all meals). Our restaurant serves authentic Karnataka cuisine and popular Indian dishes.`
        },
        {
          question: 'Do you have WiFi and other modern amenities?',
          answer: `Yes, we provide complimentary WiFi in common areas and most accommodations. Other amenities include 24/7 room service, laundry service, and recreational facilities.`
        },
        {
          question: 'Is the resort suitable for families with children?',
          answer: `Absolutely! We have family-friendly accommodations, a children's play area, safe swimming areas, and age-appropriate activities. Child safety is our top priority.`
        }
      ]
    },
    {
      category: 'Activities & Experiences',icon: 'Mountain',
      faqs: [
        {
          question: 'What adventure activities do you offer?',
          answer: `We offer white-water rafting, jungle safaris, kayaking, trekking, rock climbing, zip-lining, and night camping. All activities are conducted by certified guides with proper safety equipment.`
        },
        {
          question: 'Are activities suitable for beginners?',
          answer: `Yes! We cater to all skill levels. Our experienced guides provide proper training and safety briefings. We have beginner-friendly options for all major activities.`
        },
        {
          question: 'What should I bring for outdoor activities?',
          answer: `We provide all necessary safety equipment. We recommend bringing comfortable clothing, sports shoes, sunscreen, hat, and a change of clothes. Detailed packing lists are provided upon booking.`
        },
        {
          question: 'Are activities weather dependent?',
          answer: `Some outdoor activities may be affected by weather conditions. We monitor weather closely and may reschedule or offer alternative activities for safety reasons. Indoor activities are always available.`
        }
      ]
    },
    {
      category: 'Policies & Guidelines',icon: 'FileText',
      faqs: [
        {
          question: 'What are your check-in and check-out times?',
          answer: `Check-in: 2:00 PM onwards\nCheck-out: 11:00 AM\nEarly check-in and late check-out may be available subject to availability and additional charges.`
        },
        {
          question: 'Do you allow pets?',
          answer: `We are a pet-friendly resort! Well-behaved pets are welcome with prior notification. Additional cleaning charges may apply. Please inform us about your pet during booking.`
        },
        {
          question: 'What is your age policy for activities?',
          answer: `Age restrictions vary by activity. River rafting: 12+ years, Jungle safari: All ages welcome, Rock climbing: 10+ years. Children must be accompanied by adults for all activities.`
        },
        {
          question: 'Do you have any dress code requirements?',
          answer: `Casual, comfortable clothing is recommended. For dining, smart casual attire is preferred. For activities, we recommend sports/outdoor clothing and closed-toe shoes.`
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  return (
    <div className="bg-card rounded-lg shadow-organic p-6 lg:p-8">
      <div className="flex items-center space-x-3 mb-8">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="HelpCircle" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-card-foreground">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-muted-foreground">
            Find answers to common questions about our resort and services
          </p>
        </div>
      </div>
      <div className="space-y-8">
        {faqCategories?.map((category, categoryIndex) => (
          <div key={categoryIndex}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-secondary/10 rounded-lg">
                <Icon name={category?.icon} size={16} className="text-secondary" />
              </div>
              <h3 className="text-lg font-heading font-medium text-card-foreground">
                {category?.category}
              </h3>
            </div>

            <div className="space-y-3">
              {category?.faqs?.map((faq, faqIndex) => {
                const isOpen = openFAQ === `${categoryIndex}-${faqIndex}`;
                return (
                  <div
                    key={faqIndex}
                    className="border border-border rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-micro"
                    >
                      <span className="font-medium text-card-foreground pr-4">
                        {faq?.question}
                      </span>
                      <Icon
                        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                        size={16}
                        className="text-muted-foreground flex-shrink-0"
                      />
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 border-t border-border">
                        <div className="pt-3 text-muted-foreground whitespace-pre-line">
                          {faq?.answer}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* Contact for More Help */}
      <div className="mt-8 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="MessageCircle" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="font-medium text-card-foreground mb-1">
              Still have questions?
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Our team is here to help! Contact us directly for personalized assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => window.location.href = 'tel:+919876543210'}
                className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-micro"
              >
                <Icon name="Phone" size={14} />
                <span>Call us: +91 98765 43210</span>
              </button>
              <button
                onClick={() => window.open('https://wa.me/919876543210?text=Hi! I have a question about Dandeli Adventure Resorts.', '_blank')}
                className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-micro"
              >
                <Icon name="MessageCircle" size={14} />
                <span>WhatsApp us</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;